import React from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Home, 
  BarChart3, 
  FileSearch, 
  History, 
  FileText, 
  Settings,
  LogOut,
  User,
  Type
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface DashboardTopbarProps {
  onStartNewAnalysis?: () => void;
  showStartNewButton?: boolean;
}

const DashboardTopbar: React.FC<DashboardTopbarProps> = ({ 
  onStartNewAnalysis, 
  showStartNewButton = false 
}) => {
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  
  // Debug logging
  console.log('DashboardTopbar rendered, current path:', location.pathname);

  // Define page information based on current route
  const getPageInfo = () => {
    switch (location.pathname) {
      case "/dashboard":
        return {
          title: "Dashboard",
          description: "Monitor your audit analysis progress and insights",
          icon: <Home className="h-5 w-5" />
        };
      case "/dashboard/Results-Analyzer":
        return {
          title: "Results Analyser",
          description: "AI-powered analysis of results reports with individual model settings",
          icon: <FileSearch className="h-5 w-5" />
        };
      case "/dashboard/history":
        return {
          title: "Analysis History",
          description: "View and manage your previous audit analyses",
          icon: <History className="h-5 w-5" />
        };
      case "/dashboard/checklists":
        return {
          title: "Checklists",
          description: "Manage and customize your audit checklists",
          icon: <FileText className="h-5 w-5" />
        };
      case "/dashboard/settings":
        return {
          title: "Settings",
          description: "Configure your account and application preferences",
          icon: <Settings className="h-5 w-5" />
        };
      case "/dashboard/financial-analyzer":
        return {
          title: "📊 Castings",
          description: "Upload a PDF financial statement and let AI automatically extract financial data",
          icon: <BarChart3 className="h-5 w-5" />
        };
      case "/dashboard/spell-check":
        return {
          title: "Spell Check",
          description: "Check spelling errors in your documents using AI",
          icon: <Type className="h-5 w-5" />
        };
      default:
        return {
          title: "Dashboard",
          description: "PKF Audit AI Platform",
          icon: <Home className="h-5 w-5" />
        };
    }
  };

  const pageInfo = getPageInfo();

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Debug indicator */}
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <img 
                src="/pkf-logo.png" 
                alt="PKF Logo" 
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:block h-6 w-px bg-gray-300"></div>
            <div className="hidden md:flex items-center space-x-3">
              <div className="text-blue-600">
                {pageInfo.icon}
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">{pageInfo.title}</h1>
                <p className="text-sm text-gray-500">{pageInfo.description}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            {showStartNewButton && onStartNewAnalysis && (
              <Button variant="outline" onClick={onStartNewAnalysis} size="sm">
                Start New Analysis
              </Button>
            )}
            
            {/* User Account Dropdown */}
            {currentUser && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2 px-3 py-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    <div className="hidden md:block text-left">
                      <div className="text-sm font-medium text-gray-900">
                        {currentUser.displayName || currentUser.email?.split('@')[0] || 'User'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {currentUser.email}
                      </div>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        
        {/* Mobile page info */}
        <div className="md:hidden pb-4">
          <div className="flex items-center space-x-3">
            <div className="text-blue-600">
              {pageInfo.icon}
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{pageInfo.title}</h1>
              <p className="text-sm text-gray-500">{pageInfo.description}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTopbar;
